<script setup>
/**
 * ImmersivePane
 * One full item "page" of the immersive catalog: hero image, info row,
 * identity, smart price, quick-add and the quantity-discount ladder. The
 * overlay renders THREE of these (prev / current / next) stacked on the swipe
 * track, so the neighbor is already visible under the finger mid-drag.
 *
 * Each pane owns its own useItemPricing instance (the shared pricing single
 * source of truth), so neighbor panes always show correct effective prices.
 * The pane reflows through three responsive grid personalities (mobile stack /
 * tablet control band / PC editorial split) via the same media breakpoints as
 * the rest of the app. All sizes come from the --imm-* tokens declared on
 * .immersive-overlay.
 *
 * Emits 'celebrate' ({ kind, label }) when the CURRENT pane's cart quantity
 * newly reaches an amount-discount tier or a madregot promo step, and
 * 'hint' ({ text, gold }) on qty changes that leave the item short of the next
 * promo step — the host overlay surfaces it as a transient toast (an in-layout
 * hint would grow the panel; see ImmersivePriceArea).
 */
import { computed, watch } from 'vue'
import { useItemPricing } from '../../../../core/composables/useItemPricing.js'
import { useCartStore } from '../../../../app/store/cart.js'
import { getDisplayExpDate } from '../item-dialog/itemDialogUtils.js'
import RabanutSign from '../../../../assets/utils/RabanutSign.png'
import ImmersivePriceArea from './ImmersivePriceArea.vue'
import ImmersiveQuickAdd from './ImmersiveQuickAdd.vue'
import ImmersiveDiscountBar from './ImmersiveDiscountBar.vue'

const props = defineProps({
  item: { type: Object, required: true },
  showPrices: { type: Boolean, default: true },
  // Only the current pane fires celebrations (neighbors sharing a paritMerakez
  // group would otherwise fire duplicates).
  isCurrent: { type: Boolean, default: false },
})

const emit = defineEmits(['celebrate', 'hint'])

const cartStore = useCartStore()

// ── Pricing (single source of truth, per pane) ──────────────────
const itemRef = computed(() => props.item)
const {
  isOutOfStock,
  hasValidPrice,
  cartQty,
  isInCart,
  isOnlyBox,
  boxSize,
  stepSize,
  cumulativePromoResult,
  hasPromo,
  hasAnyEligiblePromo,
  promoIsFirstOrder,
  hasDiscount,
  discountDisplayInfo,
  discountLadder,
  discountTotalQty,
  promoLadder,
  formattedPrice,
  originalPrice,
  lineTotalWithVat,
  nextStepHintWithBoxes,
} = useItemPricing(itemRef)

const discountPercent = computed(() => discountDisplayInfo.value?.discountPercent || 0)

// ── Step rail (physical-left ladder) ─────────────────────────────
// One rail per pane: a madregot PROMO rail (red / gold for first-order) when
// the item belongs to a madregot promotion, else the amount-DISCOUNT rail
// (green). Pricing rules guarantee they never apply together.
const promoRail = computed(() => {
  if (!props.showPrices) return null
  const steps = promoLadder.value
  if (!steps || steps.length === 0 || !originalPrice.value) return null
  const ladder = [
    { minQty: 0, unitPriceAfterDiscount: originalPrice.value, isBase: true, isActive: false },
    ...steps.map((s) => ({
      minQty: s.amount,
      unitPriceAfterDiscount: s.price.toFixed(2),
      isActive: s.isActive,
    })),
  ]
  return {
    variant: 'promo',
    gold: promoIsFirstOrder.value,
    caption: promoIsFirstOrder.value ? 'מבצע חדשים' : 'מבצע',
    ladder,
    totalQty: cumulativePromoResult.value?.application?.totalQty || 0,
  }
})

const discountRail = computed(() => {
  if (!props.showPrices) return null
  if (!discountLadder.value || discountLadder.value.length === 0) return null
  return {
    variant: 'discount',
    gold: false,
    caption: null,
    ladder: discountLadder.value,
    totalQty: discountTotalQty.value,
  }
})

const rail = computed(() => promoRail.value || discountRail.value)

// Promo price range shown under the badge+price row: full (base) price down
// to the best step price, e.g. "₪3.85 - ₪3.54". Static per item — visible
// before the first unit is added, like the promotions-department cards.
const promoPriceRange = computed(() => {
  if (!props.showPrices) return null
  const steps = promoLadder.value
  if (!steps || steps.length === 0 || !originalPrice.value) return null
  const best = Math.min(...steps.map((s) => s.price))
  if (!(best > 0)) return null
  return `₪${originalPrice.value} - ₪${best.toFixed(2)}`
})

// Drives BOTH the rail v-if and the pane's has-ladder/no-ladder grid class, so
// the rail column/gutter is reserved only when a rail is actually shown.
const hasLadder = computed(() => !!rail.value && rail.value.ladder.length > 1)

// Tap a rail tier -> raise THIS item's qty just enough for the GROUP total
// (promo makats / paritMerakez) to reach the tier, rounded up to a box
// multiple for box-only items so the tier is actually met.
function onSelectTier(minQty) {
  const makat = props.item?.makat
  if (!makat || minQty <= 0 || !rail.value) return
  const othersQty = Math.max(0, rail.value.totalQty - cartQty.value)
  let target = Math.max(0, minQty - othersQty)
  if (target <= 0) return
  if (isOnlyBox.value && boxSize.value > 1) {
    target = Math.ceil(target / boxSize.value) * boxSize.value
  }
  cartStore.setQty(makat, target)
}

const imageUrl = computed(() => {
  const img = props.item?.image
  return typeof img === 'string' && img.trim().length > 0 ? img : null
})

// Rabanut (chief rabbinate) approval — mirrors the catalog card (kosher_rabanut 1/2)
const showRabanutSign = computed(
  () => props.item?.kosher_rabanut === '1' || props.item?.kosher_rabanut === '2'
)

// Expiry date (only when valid and >= 30 days away — shared rule with the dialog)
const displayExpDate = computed(() => getDisplayExpDate(props.item))

// ── Celebration triggers (current pane only) ────────────────────
// Amount-discount tier newly reached / deepened: the applied percent rose.
watch(discountPercent, (nv, ov) => {
  if (!props.isCurrent) return
  if (nv > (ov || 0) && cartQty.value > 0) {
    emit('celebrate', { kind: 'discount', label: `-${nv}%` })
  }
})

// Madregot promo step newly reached / deepened: the applied unit price dropped
// (entering the promo counts too). Decreasing qty back up a step never fires.
const appliedPromoPrice = computed(() => {
  const p = cumulativePromoResult.value?.application?.appliedStep?.unitPrice
  return typeof p === 'number' && p > 0 ? p : null
})

watch(appliedPromoPrice, (nv, ov) => {
  if (!props.isCurrent) return
  if (nv != null && (ov == null || nv < ov) && cartQty.value > 0) {
    emit('celebrate', { kind: 'promo', label: `₪${nv.toFixed(2)}` })
  }
})

// ── Next-step hint (current pane only) ──────────────────────────
// Any qty change that leaves the item in-cart but short of the next promo step
// re-emits the hint; the host shows/refreshes the toast (which reads as a
// live-updating counter under rapid taps and fades after the last one).
watch(cartQty, (nv, ov) => {
  if (!props.isCurrent || nv === ov || !(nv > 0)) return
  const hint = nextStepHintWithBoxes.value
  if (!hint) return
  const boxes = hint.boxesText ? ` (≈ ${hint.boxesText})` : ''
  emit('hint', {
    text: `קנה עוד ${hint.unitsNeeded} יח'${boxes} למחיר ₪${hint.nextPrice.toFixed(2)} ליחידה`,
    gold: promoIsFirstOrder.value,
  })
})
</script>

<template>
  <div class="immersive-pane" :class="{ 'has-ladder': hasLadder, 'no-ladder': !hasLadder }">
    <!-- Corner stack — pinned to the pane's visible top (physical-right)
         corner, so it hugs the edge even inside the ladder gutter. On MOBILE
         the promo badge sits here above the makat sticker (moved out of the
         nowrap price row, where it overflowed with long prices); ≥768 the
         badge hides and the price-row badge takes over. -->
    <div class="imm-corner-stack">
      <span
        v-if="showPrices && hasValidPrice && (hasPromo || hasAnyEligiblePromo)"
        class="imm-corner-badge"
        :class="promoIsFirstOrder ? 'badge-gold' : 'badge-red'"
      >
        {{ promoIsFirstOrder ? 'מבצע לקוחות חדשים' : 'מבצע' }}
      </span>
      <span class="imm-makat-sticker">מק"ט {{ item.makat }}</span>
    </div>

    <!-- Image hero -->
    <div class="imm-image-zone">
      <img v-if="imageUrl" :src="imageUrl" :alt="item.name" class="imm-image" />
      <div v-else class="imm-image-placeholder">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>
    </div>

    <!-- Control panel: name row + the scrolling data zone. One scroll context
         per item so tall B2B content never overflows. -->
    <div class="imm-panel">
      <!-- Info row — name + expiry (the rabanut badge lives in the kosher line). -->
      <div class="imm-info-row">
        <div class="imm-info-main">
          <span class="imm-info-name">{{ item.name || 'פריט ללא שם' }}</span>
        </div>
        <span v-if="displayExpDate" class="imm-info-exp">תוקף: {{ displayExpDate }}</span>
      </div>

      <!-- Data area — scrolls internally if content is taller -->
      <div class="imm-data-zone">
        <!-- Identity: kosher certification -->
        <div v-if="item.kosher_type" class="imm-identity">
          <span class="imm-kosher">
            <img
              v-if="showRabanutSign"
              class="imm-kosher-badge"
              :src="RabanutSign"
              alt="באישור רבנות ראשית בלבד"
            />
            כשרות: {{ item.kosher_type }}
          </span>
        </div>

        <!-- Price -->
        <div class="imm-price-zone">
          <ImmersivePriceArea
            :show-prices="showPrices"
            :has-valid-price="hasValidPrice"
            :has-promo="hasPromo"
            :has-eligible-promo="hasAnyEligiblePromo"
            :promo-price-range="promoPriceRange"
            :promo-is-first-order="promoIsFirstOrder"
            :has-discount="hasDiscount"
            :discount-percent="discountPercent"
            :formatted-price="formattedPrice"
            :original-price="originalPrice"
            :line-total-with-vat="lineTotalWithVat"
            :is-in-cart="isInCart"
          />
        </div>

        <!-- Quick add -->
        <div v-if="showPrices" class="imm-quickadd-zone">
          <ImmersiveQuickAdd
            :item="item"
            :is-only-box="isOnlyBox"
            :box-size="boxSize"
            :step-size="stepSize"
            :has-valid-price="hasValidPrice"
            :is-out-of-stock="isOutOfStock"
          />
        </div>
      </div>

      <!-- Barcode — pinned to the very bottom of the card -->
      <div v-if="item.barcode" class="imm-barcode imm-barcode-row">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <line x1="4" y1="6" x2="4" y2="18" />
          <line x1="7" y1="6" x2="7" y2="18" />
          <line x1="10" y1="6" x2="10" y2="18" />
          <line x1="14" y1="6" x2="14" y2="18" />
          <line x1="17" y1="6" x2="17" y2="18" />
          <line x1="20" y1="6" x2="20" y2="18" />
        </svg>
        {{ item.barcode }}
      </div>
    </div>

    <!-- Step rail (physical left): promo madregot (red/gold) or quantity
         discount (green). Inside the pane so it slides as a unit with the item. -->
    <ImmersiveDiscountBar
      v-if="hasLadder"
      class="imm-disc"
      :variant="rail.variant"
      :gold="rail.gold"
      :caption="rail.caption"
      :ladder="rail.ladder"
      :total-qty="rail.totalQty"
      @select-tier="onSelectTier"
    />
  </div>
</template>

<style scoped>
.immersive-pane {
  position: relative;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  /* One pane reflowed through three personalities. Mobile/Tablet: a simple
     vertical stack (image over panel) — the discount ladder is NOT a grid track
     here but an ABSOLUTE overlay sitting in the physical-left padding gutter (see
     below), so it never shifts the centering of the image / name / price /
     quick-add. PC promotes the ladder to a real grid column (editorial split).
     RTL: the FIRST area token is inline-start = physical RIGHT. */
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: minmax(0, 38%) minmax(0, 1fr);
  grid-template-areas:
    'image'
    'panel';
}

/* With a ladder, reserve a SYMMETRIC horizontal gutter so the content stays
   perfectly centred while the overlaid ladder lives in the (physical-left) gutter. */
.immersive-pane.has-ladder {
  padding-inline: var(--imm-disc-gutter, 72px);
}

/* Grid regions must be allowed to shrink so contain/scroll math holds. */
.immersive-pane > * {
  min-width: 0;
  min-height: 0;
}

/* Discount ladder — absolute overlay centred in the physical-left padding gutter
   (mobile/tablet). Out of flow, so it never affects the centering of the other
   elements. :deep() pierces the child component's scope boundary. Full height so
   the proportional checkpoints + cumulative green fill read correctly. PC resets
   this to a static grid column. */
.immersive-pane :deep(.imm-disc) {
  position: absolute;
  inset-block: 6px;
  inset-inline-end: calc((var(--imm-disc-gutter, 72px) - var(--imm-disc-w, 64px)) / 2);
  z-index: 4;
}

/* Panel = name row + data zone; owns the internal scroll (one context per item)
   so tall B2B content never overflows the 100dvh frame. */
.imm-panel {
  grid-area: panel;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.imm-image-zone {
  grid-area: image;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8px 12px;
}

/* Corner stack — anchored to the PANE (not the image zone), so it sits flush
   with the visible top physical-right corner even when the ladder gutter
   insets the image zone. Column: (mobile promo badge) over the makat sticker. */
.imm-corner-stack {
  position: absolute;
  top: 8px;
  inset-inline-start: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  /* Inline start = physical RIGHT in RTL — both pills hug the corner edge. */
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;
}

/* Mobile-only promo badge (same identity pill as the price-row badge). */
.imm-corner-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #ffffff;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.imm-corner-badge.badge-red {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.imm-corner-badge.badge-gold {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

/* Tablet/PC keep the badge inside the price row (there's width to spare). */
@media (min-width: 768px) {
  .imm-corner-badge {
    display: none;
  }
}

.imm-makat-sticker {
  padding: 4px 10px;
  border-radius: 10px;
  border: 1px solid var(--imm-pill-border, rgba(255, 255, 255, 0.14));
  background: var(--imm-pill-bg, rgba(15, 23, 42, 0.55));
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.85));
  font-size: var(--imm-ident-size, 0.78rem);
  font-weight: 700;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.imm-image {
  max-width: 100%;
  /* Fit the cell, but never exceed the per-breakpoint cap (keeps portrait images
     from dominating on PC where the image column is full height). */
  max-height: min(100%, var(--imm-img-max, 100%));
  object-fit: contain;
  /* Deep drop shadow reads well on the dark theme only — the light theme
     zeroes it via --imm-img-shadow (clean, flat hero). */
  filter: var(--imm-img-shadow, drop-shadow(0 18px 28px rgba(0, 0, 0, 0.28)));
}

.imm-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imm-image-placeholder svg {
  width: 88px;
  height: 88px;
  color: var(--imm-placeholder, rgba(255, 255, 255, 0.22));
}

/* Info row — name + expiry beneath the image: full-width, transparent,
   theme-colored text. */
.imm-info-row {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  margin: 4px 0 0;
  padding: 4px 8px;
}

.imm-info-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 100%;
}

.imm-info-name {
  font-size: var(--imm-name-size, 1.05rem);
  font-weight: 700;
  line-height: 1.25;
  color: var(--imm-text, #ffffff);
  text-align: center;
  /* Clamp long B2B names to 2 lines (ellipsis) instead of overflowing. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.imm-info-exp {
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--imm-text-soft, rgba(255, 255, 255, 0.82));
}

/* Data area — identity / price / quick-add. The panel owns the scroll, so this
   just stacks its content (the tablet & PC blocks re-lay it out). Stacks ABOVE
   the overlaid rail (z 4): the rail's nowrap tier labels can poke past the
   gutter into the content column and must never cover the controls. On PC the
   rail is a real grid column (no overlap), so this is inert there. */
.imm-data-zone {
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 12px 18px 16px;
}

.imm-identity {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.imm-kosher {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--imm-ident-size, 0.78rem);
  font-weight: 600;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.62));
}

.imm-kosher-badge {
  width: 18px;
  height: auto;
  background: #ffffff;
  border-radius: 3px;
  padding: 1px;
}

.imm-barcode {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--imm-ident-size, 0.78rem);
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--imm-text-muted, rgba(255, 255, 255, 0.6));
  font-variant-numeric: tabular-nums;
}

.imm-barcode svg {
  width: 22px;
  height: 16px;
  opacity: 0.8;
}

/* Barcode row — pinned to the very bottom of the panel (the auto margin eats
   the free space above it, so it hugs the card's bottom edge). */
.imm-barcode-row {
  margin-top: auto;
  justify-content: center;
  padding: 8px 8px 6px;
  flex: 0 0 auto;
}

.imm-price-zone {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.imm-quickadd-zone {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 2px;
}

/* ── Mobile + discount ladder: the ladder gutter narrows the content column.
   The standalone trash leaves the stepper row on mobile (ImmersiveQuickAdd
   moves it to the meta line), so the big +/- still fit and stay centered. ── */
@media (max-width: 767px) {
  .immersive-pane.has-ladder {
    --imm-step-size: 60px;
    --imm-qty-w: 68px;
  }

  .immersive-pane.has-ladder .imm-data-zone {
    padding-inline: 8px;
  }
}

/* Very narrow phones: compact control sizes so the stepper never clips inside
   the ladder gutter (content column ≈ screen − 2×72px gutter). */
@media (max-width: 379px) {
  .immersive-pane.has-ladder {
    --imm-step-size: 52px;
    --imm-qty-w: 60px;
  }
}

/* ── Tablet (768–1279): more vertical than PC, wider than mobile. Large hero,
   lower half becomes a price | quick-add control band, vertically centred. ── */
@media (min-width: 768px) and (max-width: 1279px) {
  .immersive-pane {
    grid-template-rows: minmax(0, 47%) minmax(0, 1fr);
  }

  /* Centre the name + controls cluster in the lower half so the screen reads as
     full instead of top-heavy with an empty band below. `safe` falls back to
     start-aligned if a tall item would otherwise clip above the scroll. The
     info-row's auto top margin pairs with the barcode row's (bottom-pinned), so
     the free space splits evenly — cluster centred, barcode at the very bottom. */
  .imm-panel {
    justify-content: safe center;
  }

  .imm-info-row {
    margin-top: auto;
  }

  /* Keep the mobile COLUMN stack (identity / price / quick-add) — tablet has
     vertical room to spare, while a side-by-side price|quick-add band clips
     the stepper inside the ladder gutter (content ≈ width − 2×100px). Only the
     spacing scales up. */
  .imm-data-zone {
    gap: 18px;
    padding: 12px 22px 20px;
  }
}

/* ── PC (≥1280): full-width editorial split — hero image + controls + ladder.
   RTL -> image is physical RIGHT (hero), panel centre, ladder physical LEFT. ── */
@media (min-width: 1280px) {
  .immersive-pane {
    grid-template-columns: minmax(0, 1.35fr) minmax(400px, 0.9fr) auto;
    grid-template-rows: 1fr;
    grid-template-areas: 'image panel ladder';
    column-gap: clamp(16px, 2vw, 40px);
  }

  /* PC promotes the ladder to a real column, so drop the mobile/tablet gutter. */
  .immersive-pane.has-ladder {
    padding-inline: 0;
  }

  .immersive-pane.no-ladder {
    grid-template-columns: minmax(0, 1.4fr) minmax(440px, 0.85fr);
    grid-template-areas: 'image panel';
  }

  /* Ladder returns to flow as a real grid column (physical far-left) and stretches
     to full row height so the proportional track + green fill read as a tall rail. */
  .immersive-pane :deep(.imm-disc) {
    position: static;
    inset: auto;
    grid-area: ladder;
    align-self: stretch;
  }

  /* Editorial panel: vertically centred, content hugs the inline-start (physical
     right) edge. `safe center` falls back to start-aligned if a tall item ever
     overflows, so nothing clips above the scroll. */
  .imm-panel {
    justify-content: safe center;
    padding-inline: clamp(20px, 3vw, 48px);
  }

  /* Reset the tablet control-band grid back to a single editorial column that
     hugs the start (physical right) edge. */
  .imm-data-zone {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: start;
    gap: 16px;
    padding-inline: 0;
  }

  /* Name hugs the start (physical-right) edge, not a full-width banner. The
     auto top margin pairs with the bottom-pinned barcode row (twin autos →
     cluster stays vertically centred, barcode hugs the bottom). */
  .imm-info-row {
    align-self: flex-start;
    width: auto;
    margin-inline: 0;
    margin-top: auto;
    align-items: flex-start;
  }

  .imm-barcode-row {
    justify-content: flex-start;
  }

  .imm-info-main {
    justify-content: flex-start;
  }

  .imm-info-name {
    text-align: start;
  }

  /* Dignified vertical property list. */
  .imm-identity {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  /* Price as a start-aligned headline — reach into the price child. */
  .imm-price-zone {
    width: 100%;
    justify-content: flex-start;
  }

  .imm-price-zone :deep(.imm-price),
  .imm-price-zone :deep(.imm-price-main) {
    align-items: flex-start;
    text-align: start;
  }

  .imm-price-zone :deep(.imm-price-main) {
    justify-content: flex-start;
  }

  /* Quick-add hugs the start edge too. */
  .imm-quickadd-zone {
    width: 100%;
    justify-content: flex-start;
  }

  .imm-quickadd-zone :deep(.imm-quickadd) {
    align-items: flex-start;
  }
}
</style>
